/**
 * Toucan Reads — draining the queue of links nobody has looked up yet.
 *
 * `fetchedAt` absent is the whole queue, and three things drain it: an item's
 * edit page while it is open, this sweep while the library is on screen, and
 * ImportPage, which shows this sweep's progress. Android freezes a
 * backgrounded PWA mid-fetch, so a share left half done is picked up by
 * whichever comes next.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import { flushBlobs } from "toucan";
import { enrich } from "./enrich.ts";
import { nextUnsettled, unsettledItems } from "./model.ts";
import { store } from "./state.ts";

/**
 * Failures in a row before the sweep gives up until the next visit. One is an
 * awkward host; three in a row is the device being offline, and asking again
 * every second achieves nothing.
 */
const GIVE_UP_AFTER = 3;

/**
 * The sweep's state, for a screen that is telling somebody about it.
 * `stalled` is a run that gave up with work left, which a screen watching the
 * queue alone would go on calling work in progress.
 */
export type SweepState = "idle" | "working" | "stalled";

/** The outside work a sweep does, so a caller can replace it. */
export interface SweepWork {
  lookUp?: typeof enrich;
  sendUp?: typeof flushBlobs;
}

/**
 * Work through the queue one item at a time while `running`, oldest save
 * first. Every request goes through the one proxy, which paces itself per host
 * anyway, and a large library would otherwise open a fetch per item at once.
 *
 * What is left is a question for the store, which every page reading it
 * re-renders on; what comes back from here is only whether the sweep is still
 * getting anywhere.
 */
export function useSweep(
  running: boolean,
  /**
   * How one item is looked up, and what to do with whatever it stored. Named
   * as arguments so a test can hand over stand-ins, rather than reaching
   * around the module loader to replace imports it has already resolved.
   */
  { lookUp = enrich, sendUp = flushBlobs }: SweepWork = {},
): SweepState {
  // How much is left also says whether there is anything to start for. A new
  // share or paste while the library is already open changes it and sets the
  // sweep going again.
  const left = unsettledItems(store()).length;
  const [stalled, setStalled] = useState(false);
  // Read through a ref rather than named in the effect's dependencies: a run
  // restarts when there is more to do or the screen comes and goes, and a
  // caller passing a fresh function each render is neither of those.
  const work = useRef({ lookUp, sendUp });
  work.current = { lookUp, sendUp };
  useEffect(() => {
    if (!running || left === 0) {
      return undefined;
    }
    let sweeping = true;
    setStalled(false);
    // Items this run could not settle. A host that is rate-limiting answers
    // the same way every time, and the queue behind it should not wait.
    const failed = new Set<string>();
    // Every item this run has asked about. A run asks once, because an item
    // can be left in the queue by a pass that answered — an article whose
    // reading did not survive the fetch it came from — and asking again inside
    // the same run is a loop with a fetch in it.
    const asked = new Set<string>();
    const drain = async (): Promise<void> => {
      let inARow = 0;
      for (;;) {
        const item =
          inARow < GIVE_UP_AFTER ? nextUnsettled(store(), asked) : undefined;
        if (item === undefined) {
          // Either everything answered, or what is left has refused this run;
          // see `SweepState`. Preact ignores the call on an unmounted page.
          setStalled(failed.size > 0);
          return;
        }
        const id = item.id;
        asked.add(id);
        if (await work.current.lookUp(store(), item, Date.now())) {
          inARow = 0;
        } else {
          failed.add(id);
          inARow += 1;
        }
        if (!sweeping) {
          return;
        }
      }
    };
    // Whatever the pass stored — an article's text, a paper's first page —
    // goes up once at the end of it rather than per item, since uploading
    // walks every blob this device holds.
    void drain().finally(() => {
      void work.current.sendUp();
    });
    return () => {
      sweeping = false;
    };
  }, [running, left]);
  if (!running || left === 0) {
    return "idle";
  }
  return stalled ? "stalled" : "working";
}
